/**
 * Builds the explicit, LinkedIn-style location string for a remote listing.
 *
 * The problem this solves: aggregator ingestion flattens ~99.9% of jobs to
 * `location = "Remote"` and ~95% to `remoteType = "worldwide"`, so the bare
 * metadata renders as the implicit, confusing "Worldwide Remote · Remote".
 *
 * The fix reuses the signals `detectGeoRestriction()` already computes (region
 * restriction + required timezone + WIB overlap) — which were previously
 * discarded — to produce the MOST explicit phrasing the data supports:
 *
 *   - Region-restricted (e.g. US-only)   → "United States · Remote"
 *   - Restricted, region unresolved      → "Remote · Some regions only"
 *   - Timezone-required (e.g. EST hours) → "Remote · US East hours"
 *   - Worldwide remote, CONFIRMED open   → "Remote · Open worldwide"
 *   - Worldwide remote, not confirmed    → "Remote · Countries not stated"
 *   - Regional remote (no region known)  → "Remote · Regional"
 *   - Anything else                      → "Remote"
 *
 * "Open worldwide" (not the bare, ambiguous "Worldwide") makes the confirmed
 * bucket explicit: it reads as "this employer accepts applicants from anywhere",
 * which is the actual meaning, rather than a vague geographic adjective.
 *
 * 🔴 2026-09-13 (user-reported): that claim used to be derived from
 * `remote_type` ALONE. Ingestion writes `remote_type = "worldwide"` for ~95% of
 * aggregator rows whether or not the employer is open to every country, so a
 * US-only CircleCI NAMER role (`geo_fit = "unknown"`) rendered
 * "Remote · Open worldwide" right next to a <GeoFitBadge> saying the employer
 * did not state openness. Prod D1 at the time: 2,153 active `worldwide` rows
 * with `geo_fit = "unknown"` and 526 with `geo_fit = "restricted"` — every one
 * of them making a promise to an Indonesian seeker that the data cannot back.
 *
 * The rule now matches `src/lib/seo/job-faq.ts` (PR #5288) exactly, so a job
 * page's label, its FAQ and its badge can never contradict each other:
 * **openness is claimed ONLY when `geo_fit === "id_friendly"` positively
 * confirms it.** Absent or unknown eligibility is stated as unknown, never as
 * open. `jobLocationDisplay()` is the shared entry point that applies it — use
 * it rather than re-deriving the phrasing per surface.
 *
 * Pure (no I/O, no AI) — safe on the render path. Returns structured parts so
 * the caller controls styling; `text` is the joined "A · B" convenience string.
 */

import {
	detectGeoRestriction,
	type GeoFit,
	type GeoRestriction,
} from "@/lib/jobs/geo-restriction";

/** Human-readable region label for a restriction region code. */
export const REGION_DISPLAY: Readonly<Record<string, string>> = {
	US: "United States",
	"North America": "North America",
	EU: "European Union",
	UK: "United Kingdom",
	Canada: "Canada",
	APAC: "Asia-Pacific",
	LATAM: "Latin America",
};

/**
 * Region-scoped remote values ingestion writes to `jobs.remote_type`. Both mean
 * "remote, but only from certain places" — `remote-region` was the value 1,380
 * active rows carried while this module only recognized `regional`, so they fell
 * through to the plain-remote fallback. Exported because `src/lib/seo/job-faq.ts`
 * classifies the same column and a second private copy is how the FAQ and the
 * label drift apart.
 */
export const REGIONAL_REMOTE_TYPES: ReadonlySet<string> = new Set([
	"regional",
	"remote-region",
]);

/**
 * Qualifier for a remote role whose eligibility the employer never stated —
 * `geo_fit` "unknown" or null. Deliberately says what is missing rather than
 * implying either answer: this is the bucket that used to read "Open worldwide".
 */
const COUNTRIES_NOT_STATED = "Countries not stated";

/**
 * Qualifier for a `geo_fit = "restricted"` verdict whose region neither the
 * listing text nor `applicant_region` could resolve. 526 active `worldwide`
 * rows were in exactly this state and read as "Open worldwide" — the precise
 * inversion of the truth.
 */
const SOME_REGIONS_ONLY = "Some regions only";

/** Qualifier for an in-office role (`remote_type = "onsite"`). */
const ON_SITE = "On-site";

/**
 * Human-readable "work hours" label for a required timezone. Keeps it short and
 * region-anchored so a reader instantly grasps the overlap cost. Explicit UTC
 * offsets (e.g. "UTC+8") fall through to the raw token.
 */
const TZ_DISPLAY: Readonly<Record<string, string>> = {
	EST: "US East hours",
	CST: "US Central hours",
	MST: "US Mountain hours",
	PST: "US West hours",
	GMT: "UK hours",
	CET: "Europe hours",
	IST: "India hours",
	SGT: "SG/HK hours",
	JST: "Japan hours",
	AEST: "Australia hours",
};

export type LocationDisplayKind =
	| "restricted"
	| "timezone"
	| "worldwide"
	| "regional"
	| "unstated"
	| "remote"
	| "onsite";

export interface LocationDisplay {
	/** The primary scope chip, e.g. "United States" or "Remote". */
	scope: string;
	/** The secondary qualifier chip, e.g. "Remote", "Open worldwide", "US East hours". null when none. */
	qualifier: string | null;
	/** Joined "scope · qualifier" (or just "scope") — the convenience string. */
	text: string;
	/**
	 * Semantic kind for callers that want to style by meaning:
	 *  - "restricted"  region-locked (warn-styling appropriate)
	 *  - "timezone"    worldwide-remote but with a required work-hours window
	 *  - "worldwide"   CONFIRMED open worldwide (geo_fit id_friendly), no tz
	 *  - "regional"    regional remote, no specific region resolved
	 *  - "unstated"    remote, but the employer never stated who may apply
	 *  - "remote"      plain remote, no further signal
	 *  - "onsite"      in-office role, never labelled remote
	 */
	kind: LocationDisplayKind;
}

function timezoneLabel(tz: string | null): string | null {
	if (!tz) return null;
	return TZ_DISPLAY[tz] ?? tz;
}

/**
 * Returns the raw location only when it is a real place name worth showing —
 * i.e. not a generic "remote"/"anywhere"/"worldwide" synonym (already conveyed
 * by the scope/qualifier) and not empty.
 */
function meaningfulPlace(rawLocation?: string | null): string | null {
	if (!rawLocation) return null;
	const trimmed = rawLocation.trim();
	if (!trimmed) return null;
	if (
		/^(remote|anywhere|worldwide|world[\s-]?wide|global(?:ly)?)$/i.test(trimmed)
	) {
		return null;
	}
	return trimmed;
}

/**
 * Compute the explicit location display from the structured signals.
 *
 * `geo` comes from `detectGeoRestriction()`; `remoteType` is the raw column
 * (`worldwide` / `regional` / `timezone-based` / null). `rawLocation` is the
 * (almost always "Remote") free-text column — only appended when it carries a
 * real place name we don't already convey (never the bare "remote").
 *
 * `companyLocation` is the enriched `jobs.company_location` column (the
 * researched company HQ, e.g. "Singapore", "San Francisco, USA"). It is the
 * DATA half of the explicit-location work: for a CONFIRMED worldwide-remote or
 * a plain-remote listing, the company's real base is more informative than the
 * generic "anywhere" phrasing, so we surface it as "Remote · Singapore".
 *
 * It is NOT used to override an eligibility signal (region restriction /
 * required timezone / an unstated verdict): those tell the seeker whether they
 * can be hired at all and must stay primary; the company HQ could otherwise
 * mislead (a US-only role HQ'd in Berlin is still US-only, and an unconfirmed
 * role HQ'd in Singapore is still unconfirmed — "Remote · Singapore" there just
 * swaps one unsupported claim for another).
 *
 * `applicantRegion` is the preserved-at-ingest `jobs.applicant_region` token
 * (US / North America / EU / UK / Canada / APAC / LATAM, or null). It is the
 * source's STRUCTURED region restriction for a region-scoped remote role whose
 * geography the pipeline previously flattened away. It is consulted only when
 * the description-derived geo restriction did not already fire (that body-text
 * signal is more specific), and renders as "Remote · EU".
 */
export function buildLocationDisplay(
	geo: GeoRestriction,
	remoteType?: string | null,
	rawLocation?: string | null,
	companyLocation?: string | null,
	applicantRegion?: string | null,
): LocationDisplay {
	const tz = timezoneLabel(geo.timezoneRequired);
	const hq = meaningfulPlace(companyLocation);

	// 0. In-office role. Checked first: every branch below describes a remote
	// listing and ends in a "Remote" qualifier, which is the wrong claim here.
	if (remoteType === "onsite") {
		const place = meaningfulPlace(rawLocation);
		if (place) {
			return {
				scope: place,
				qualifier: ON_SITE,
				text: `${place} · ${ON_SITE}`,
				kind: "onsite",
			};
		}
		return { scope: ON_SITE, qualifier: null, text: ON_SITE, kind: "onsite" };
	}

	// 1. Region restriction is the strongest, most explicit signal.
	//
	// Keyed on `regions` rather than on `fit`: `detectGeoRestriction` populates
	// `regions` ONLY from its restricted branch, so a non-empty list is itself a
	// restriction finding. Requiring `fit === "restricted"` too would discard a
	// region the listing text states outright whenever the stored column
	// disagrees (a card passes the stored verdict in, and 526 restricted rows
	// carry no region at all) — and losing the region there means falling
	// through to a LESS specific label, never a more permissive one.
	if (geo.regions.length > 0) {
		const region = geo.regions[0];
		const scope = REGION_DISPLAY[region] ?? region;
		// A restricted role with a timezone shows the hours; otherwise "Remote".
		const qualifier = tz ?? "Remote";
		return {
			scope,
			qualifier,
			text: `${scope} · ${qualifier}`,
			kind: "restricted",
		};
	}

	// 1b. Preserved applicant-region (e.g. "Remote · EU") — the source's
	// structured region for a region-scoped remote role. Below the body-text
	// geo restriction (more specific) but above the generic worldwide/regional
	// buckets, so a region-locked remote role no longer reads as plain "Remote".
	if (applicantRegion) {
		const regionLabel = REGION_DISPLAY[applicantRegion] ?? applicantRegion;
		const qualifier = tz ?? regionLabel;
		return {
			scope: "Remote",
			qualifier,
			text: `Remote · ${qualifier}`,
			kind: tz ? "timezone" : "regional",
		};
	}

	// 1c. A restricted verdict whose region nothing could resolve. It must still
	// read as limited: the alternative (falling through to the worldwide branch)
	// is what made 526 active restricted rows advertise themselves as open. The
	// eligibility verdict outranks a timezone hint here — "US East hours" would
	// describe the schedule while hiding the blocker.
	if (geo.fit === "restricted") {
		return {
			scope: "Remote",
			qualifier: SOME_REGIONS_ONLY,
			text: `Remote · ${SOME_REGIONS_ONLY}`,
			kind: "restricted",
		};
	}

	// 2. Worldwide-remote but with an explicit required timezone window.
	if (tz) {
		return {
			scope: "Remote",
			qualifier: tz,
			text: `Remote · ${tz}`,
			kind: "timezone",
		};
	}

	// 3. `remote_type = "worldwide"` — the claim, and the whole reason this
	//    function exists. Ingestion writes it for ~95% of aggregator rows as a
	//    shape ("this is a remote listing"), NOT as an eligibility verdict, so
	//    it alone can never license "open worldwide". Only a positive
	//    `geo_fit = "id_friendly"` can; anything else states the gap instead.
	if (remoteType === "worldwide") {
		if (geo.fit === "id_friendly") {
			// Confirmed open. The enriched HQ is more informative than the generic
			// "anywhere" phrasing when we have it, and is safe here precisely
			// because eligibility is already settled.
			const qualifier = hq ?? "Open worldwide";
			return {
				scope: "Remote",
				qualifier,
				text: `Remote · ${qualifier}`,
				kind: "worldwide",
			};
		}
		// Unknown / null. The HQ is deliberately NOT used as the fallback: on an
		// unconfirmed row "Remote · Singapore" trades one unsupported claim for
		// another (a reader takes it as the role's location), and the thing the
		// seeker needs is the disclosure that nobody said who may apply.
		return {
			scope: "Remote",
			qualifier: COUNTRIES_NOT_STATED,
			text: `Remote · ${COUNTRIES_NOT_STATED}`,
			kind: "unstated",
		};
	}

	// 4. Regional remote with no resolved region — surface a real place name: the
	//    raw location column, else the enriched company HQ, else "Regional".
	if (REGIONAL_REMOTE_TYPES.has(remoteType ?? "")) {
		const place = meaningfulPlace(rawLocation) ?? hq;
		if (place) {
			return {
				scope: place,
				qualifier: "Remote",
				text: `${place} · Remote`,
				kind: "regional",
			};
		}
		return {
			scope: "Remote",
			qualifier: "Regional",
			text: "Remote · Regional",
			kind: "regional",
		};
	}

	// 5. Fallback — plain remote. Surface a real place name (raw location, else
	//    the enriched company HQ) when present, otherwise bare "Remote".
	const place = meaningfulPlace(rawLocation) ?? hq;
	if (place) {
		return {
			scope: place,
			qualifier: "Remote",
			text: `${place} · Remote`,
			kind: "remote",
		};
	}
	return { scope: "Remote", qualifier: null, text: "Remote", kind: "remote" };
}

/**
 * Indonesian translations for the *generic* location tokens this module emits.
 * Region/timezone labels (e.g. "United States", "US East hours") and real place
 * names are intentionally left untranslated — they read the same in both locales
 * and translating proper nouns adds noise without clarity.
 */
const ID_TOKEN: Readonly<Record<string, string>> = {
	Remote: "Remote",
	"Open worldwide": "Terbuka global",
	Worldwide: "Terbuka global",
	Regional: "Regional",
	[COUNTRIES_NOT_STATED]: "Negara tidak disebutkan",
	[SOME_REGIONS_ONLY]: "Hanya wilayah tertentu",
	[ON_SITE]: "Kerja di kantor (WFO)",
};

function translateToken(token: string, locale: string): string {
	if (locale !== "id") return token;
	return ID_TOKEN[token] ?? token;
}

/**
 * Localizes a `LocationDisplay` produced by `buildLocationDisplay` (which is
 * English-only) to the viewer's locale, translating only the generic scope/
 * qualifier tokens (`Remote`, `Open worldwide`, `Regional`). Proper nouns
 * (region/timezone labels, real place names) pass through unchanged.
 *
 * Lets bilingual surfaces (homepage cards, related jobs) reuse the single
 * `buildLocationDisplay` logic instead of re-deriving the phrasing per locale.
 */
export function localizeLocationDisplay(
	display: LocationDisplay,
	locale: string,
): LocationDisplay {
	if (locale !== "id") return display;
	const scope = translateToken(display.scope, locale);
	const qualifier =
		display.qualifier === null
			? null
			: translateToken(display.qualifier, locale);
	const text = qualifier ? `${scope} · ${qualifier}` : scope;
	return { ...display, scope, qualifier, text };
}

/** The stored `jobs.geo_fit` column, narrowed. Anything unrecognized — null,
 *  an empty string, a value from a cache entry written before the column
 *  existed — is "unknown", which is the state that cannot claim openness. */
function normalizeGeoFit(stored?: string | null): GeoFit {
	return stored === "id_friendly" || stored === "restricted"
		? stored
		: "unknown";
}

/** A job row as every card/email surface has it: the display fields plus the
 *  two stored eligibility columns. `geoFit`/`applicantRegion` are optional so a
 *  caller that cannot supply them still type-checks — it just gets the
 *  unconfirmed label instead of a claim it has no evidence for. */
export interface JobLocationInput {
	title?: string | null;
	location?: string | null;
	remoteType?: string | null;
	/** Stored `jobs.geo_fit` — the ONLY thing that can license "Open worldwide". */
	geoFit?: string | null;
	/** Stored `jobs.applicant_region` (US / EU / APAC / …). */
	applicantRegion?: string | null;
	/** Enriched `jobs.company_location` (researched company HQ). */
	companyLocation?: string | null;
}

/**
 * THE entry point for rendering a job's location label. Runs the detector,
 * overrides its verdict with the stored `geo_fit` column, builds the display and
 * localizes it — the four steps every card surface previously inlined, three of
 * them while silently omitting the eligibility columns.
 *
 * That omission is the drift this function exists to prevent: the job-detail
 * page composed the stored verdict into its `GeoRestriction` before building the
 * label, and the feed row, homepage picks, related-jobs and email cards did not,
 * so the same job advertised itself as "Open worldwide" on four surfaces while
 * its own detail page knew better. Take a job row, get the honest label.
 *
 * Pass `locale` to get the viewer's language; omit it for English.
 *
 * The job-detail page deliberately does NOT use this: it has the description
 * text and already runs `detectGeoRestriction` over it once for the
 * <GeoFitBadge>, so it calls `buildLocationDisplay` with that richer result
 * rather than paying for a second regex pass on the render path.
 */
export function jobLocationDisplay(
	job: JobLocationInput,
	locale?: string,
): LocationDisplay {
	// Cards carry no description, so the detector sees title + location +
	// remoteType — enough for region/timezone phrasing when the signal is in the
	// title. The eligibility VERDICT never comes from it; that is `geoFit`.
	const detected = detectGeoRestriction({
		title: job.title,
		location: job.location,
		remoteType: job.remoteType,
	});
	const display = buildLocationDisplay(
		{ ...detected, fit: normalizeGeoFit(job.geoFit) },
		job.remoteType,
		job.location,
		job.companyLocation,
		job.applicantRegion,
	);
	return locale ? localizeLocationDisplay(display, locale) : display;
}
